import { Box, Button, Checkbox, List, Stack } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useMemo, useState } from "react"
import { StandardCombo } from "lib/@components/combos/standardCombo"
import { setFromEvent } from "lib/setFromEvent"
import { If } from "lib/@components/switch"
import { getMovableSchedules } from "routes/sfg20-admin/plugins/client-migration-tab/plugins/move-schedules/controller/get-movable-schedules"
import { emptyArray } from "lib/empty-array"
import { PagedRepeat } from "lib/@components/paged-repeat"
import { ListItemBox } from "lib/@components/ListItemBox"
import { PaginationPortal } from "lib/@components/pagination-portal"
import { Bound } from "lib/@components/binding/Bound"
import { useRefresh } from "lib/@hooks/useRefresh"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { MovableSchedule } from "routes/sfg20-admin/plugins/client-migration-tab/plugins/move-schedules/movable-schedule"
import { confirm } from "lib/@dialogs/confirm"
import { moveSchedules } from "routes/sfg20-admin/plugins/client-migration-tab/plugins/move-schedules/controller/move-schedules"

export function MoveSchedulesToOtherClient() {
    const { clients, selectedClient } = useBoundContext()
    const [target] = useState({ selected: {} })
    const [migrateClient, setMigrateClient] = useState("")
    const otherClients = useMemo(
        () => clients.filter((c) => c.id !== selectedClient).sortBy("name"),
        [selectedClient, clients]
    )
    const [search] = useSearchParam("search", "")
    const schedules = getMovableSchedules.useResults(migrateClient, search) ?? emptyArray
    const refresh = useRefresh()
    const numSelected = Object.values(target.selected).filter(Boolean).length
    return (
        <Bound target={target} refresh={refresh}>
            <Stack spacing={1} sx={{ mt: 2 }}>
                <StandardCombo
                    size="small"
                    label="Migrate From"
                    options={otherClients.map((c) => ({ label: c.name, value: c.id }))}
                    value={migrateClient}
                    onChange={setFromEvent(setMigrateClient)}
                />
                <If truthy={migrateClient && schedules !== emptyArray}>
                    <SearchBoxUsingSearchParam autoFocus />
                    <ListItemBox>
                        <Checkbox
                            sx={{ ml: 2 }}
                            indeterminate={numSelected > 0 && numSelected !== schedules.length}
                            checked={numSelected === schedules.length}
                            onClick={selectAll}
                        />
                        <Box flex={1} />
                        <PaginationPortal />
                    </ListItemBox>
                    <List dense>
                        <PagedRepeat list={schedules} item={<MovableSchedule />} />
                    </List>
                </If>
                <If truthy={numSelected}>
                    <Button variant="contained" color="primary" onClick={importSchedules}>
                        Copy {numSelected} {"schedule".pluralize(numSelected)} from &quot;
                        {clients.filter((c) => c.id === migrateClient)[0]?.name}&quot; to &quot;
                        {clients.filter((c) => c.id === selectedClient)[0]?.name}&quot;
                    </Button>
                </If>
            </Stack>
        </Bound>
    )

    async function importSchedules() {
        if (
            await confirm(
                `Copy ${numSelected} ${"schedule".pluralize(numSelected)} from "${clients.filter((c) => c.id === migrateClient)[0]?.name}" to "${clients.filter((c) => c.id === selectedClient)[0]?.name}"`
            )
        ) {
            await moveSchedules(
                migrateClient,
                Object.entries(target.selected)
                    .filter(([, v]) => !!v)
                    .map("0")
            )
            target.selected = {}
            refresh()
        }
    }

    function selectAll() {
        if (numSelected === schedules.length) {
            target.selected = {}
        } else {
            schedules.forEach(({ id }) => (target.selected[id] = true))
        }
        refresh()
    }
}
